import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { CalendarDays, MapPin, Users, ArrowRight, Compass } from 'lucide-react';

export const HeroSearch = ({ hero }) => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('dates');
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [location, setLocation] = useState('');
  const [guests, setGuests] = useState('');
  const [error, setError] = useState('');

  const today = new Date().toISOString().split('T')[0];
  const nights = useMemo(() => {
    if (!checkIn || !checkOut) return 0;
    return Math.round((new Date(checkOut) - new Date(checkIn)) / 86400000);
  }, [checkIn, checkOut]);

  const searchByDates = () => {
    if (!checkIn || !checkOut) { setError('Please select both check-in and check-out dates'); return; }
    if (nights <= 0) { setError('Check-out must be after check-in'); return; }
    setError('');
    const params = new URLSearchParams({ check_in: checkIn, check_out: checkOut });
    if (location) params.set('location', location);
    if (guests) params.set('guests', guests);
    navigate(`/venues?${params.toString()}`);
  };

  return (
    <section className="relative min-h-[92vh] flex items-center font-wvc-body" data-testid="home-hero">
      <div className="absolute inset-0">
        <img src={hero?.image} alt="Premium wedding venue" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-b from-[#1A201C]/70 via-[#1A201C]/45 to-[#1A201C]/70" />
      </div>
      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 w-full pt-28 pb-16">
        <div className="max-w-3xl">
          <p className="text-[#E5D8C6] uppercase tracking-[0.25em] text-xs mb-5">Start With Your Dates. See Venues That Are Actually Available.</p>
          <h1 className="font-wvc-display text-4xl sm:text-5xl lg:text-6xl text-white font-medium leading-[1.05] tracking-tight" data-testid="hero-headline">
            {hero?.headline}
          </h1>
          <p className="text-white/80 mt-5 text-base md:text-lg max-w-xl leading-relaxed">{hero?.copy}</p>
        </div>

        <div className="mt-10 max-w-3xl backdrop-blur-xl bg-white/90 border border-white/30 shadow-2xl rounded-2xl p-5 md:p-6" data-testid="hero-search-module">
          <div className="inline-flex bg-[#F3F1E7] rounded-full p-1 mb-5">
            <button
              onClick={() => setTab('dates')}
              className={`px-5 py-2 rounded-full text-sm transition-all duration-300 ${tab === 'dates' ? 'bg-[#1A3626] text-white' : 'text-[#1A201C] hover:bg-white/60'}`}
              data-testid="hero-tab-dates"
            >
              Search by Dates
            </button>
            <button
              onClick={() => setTab('browse')}
              className={`px-5 py-2 rounded-full text-sm transition-all duration-300 ${tab === 'browse' ? 'bg-[#1A3626] text-white' : 'text-[#1A201C] hover:bg-white/60'}`}
              data-testid="hero-tab-browse"
            >
              Browse All Venues
            </button>
          </div>

          {tab === 'dates' ? (
            <div data-testid="hero-dates-panel">
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-[#748278] flex items-center gap-1 mb-1"><CalendarDays className="w-3 h-3" strokeWidth={1.5} /> Check-in</label>
                  <input type="date" min={today} value={checkIn} onChange={e => setCheckIn(e.target.value)}
                    className="w-full h-14 rounded-lg border border-[#1A3626]/15 bg-white/60 px-3 text-sm text-[#1A201C] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#1A3626]"
                    data-testid="hero-checkin-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-[#748278] flex items-center gap-1 mb-1"><CalendarDays className="w-3 h-3" strokeWidth={1.5} /> Check-out</label>
                  <input type="date" min={checkIn || today} value={checkOut} onChange={e => setCheckOut(e.target.value)}
                    className="w-full h-14 rounded-lg border border-[#1A3626]/15 bg-white/60 px-3 text-sm text-[#1A201C] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#1A3626]"
                    data-testid="hero-checkout-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-[#748278] flex items-center gap-1 mb-1"><MapPin className="w-3 h-3" strokeWidth={1.5} /> Location <span className="normal-case tracking-normal">(optional)</span></label>
                  <input type="text" placeholder="City" value={location} onChange={e => setLocation(e.target.value)}
                    className="w-full h-14 rounded-lg border border-[#1A3626]/15 bg-white/60 px-3 text-sm text-[#1A201C] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#1A3626]"
                    data-testid="hero-location-input" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-[0.15em] text-[#748278] flex items-center gap-1 mb-1"><Users className="w-3 h-3" strokeWidth={1.5} /> Guests <span className="normal-case tracking-normal">(optional)</span></label>
                  <input type="number" min="1" placeholder="150" value={guests} onChange={e => setGuests(e.target.value)}
                    className="w-full h-14 rounded-lg border border-[#1A3626]/15 bg-white/60 px-3 text-sm text-[#1A201C] focus:bg-white focus:outline-none focus:ring-1 focus:ring-[#1A3626]"
                    data-testid="hero-guests-input" />
                </div>
              </div>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mt-4">
                <div className="text-sm text-[#3E4C43]" data-testid="hero-nights-display">
                  {nights > 0 ? `${nights} night${nights > 1 ? 's' : ''} selected` : ' '}
                  {error && <span className="text-[#9E4040] block sm:inline sm:ml-2" data-testid="hero-date-error">{error}</span>}
                </div>
                <button onClick={searchByDates}
                  className="bg-[#1A3626] text-white hover:bg-[#254834] rounded-full px-8 py-4 text-sm inline-flex items-center justify-center gap-2 transition-all duration-300"
                  data-testid="hero-search-btn">
                  {hero?.search_button || 'Show Available Venues'} <ArrowRight className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
            </div>
          ) : (
            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4" data-testid="hero-browse-panel">
              <p className="text-sm text-[#3E4C43] max-w-sm">{hero?.browse_line}</p>
              <button onClick={() => navigate('/venues')}
                className="bg-[#1A3626] text-white hover:bg-[#254834] rounded-full px-8 py-4 text-sm inline-flex items-center gap-2 transition-all duration-300 whitespace-nowrap"
                data-testid="hero-browse-btn">
                <Compass className="w-4 h-4" strokeWidth={1.5} /> {hero?.browse_button || 'Browse All Venues'}
              </button>
            </div>
          )}
        </div>

        <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2" data-testid="hero-trust-line">
          {(hero?.trust_points || []).map((t, i) => (
            <span key={i} className="text-white/75 text-xs uppercase tracking-[0.15em] flex items-center gap-2">
              <span className="w-1 h-1 rounded-full bg-[#C6A87C] inline-block" /> {t}
            </span>
          ))}
        </div>
      </div>
    </section>
  );
};
